// Static catalog of every integration the codebase knows how to handle.
//
// Imported by both the BE (server-side cardinality enforcement) and the
// FE (marketplace modal — "Add integration" picker). Adding a new
// integration = adding one entry here. See `.claude/skills/add-integration.md`
// for the full add-a-new-integration recipe.

import type { IntegrationCode } from "./codes.js";
import type { AccountKind, SettingType } from "../settings/registry.js";

export type IntegrationCardinality = "single" | "multi";

/**
 * One config value an integration needs. For multi-cardinality integrations
 * these are seeded as per-instance settings rows when an instance is created
 * (code `${integration}_${labelSlug}_${key}`), so each instance's config lives
 * in Settings → Catalog and gets the generic editor / encryption / audit for
 * free — same store single-cardinality integrations use via SETTINGS_REGISTRY.
 */
export interface ConfigField {
  /** snake_case field key, unique within the integration. */
  key: string;
  title: string;
  description?: string;
  /** Reuses the existing setting types — no bespoke editors. */
  type: SettingType;
  /** For type=`account_ids`: limit the picker to these account kinds. */
  accept_account_kinds?: readonly AccountKind[];
}

export interface CatalogEntry {
  /** Display title shown in the marketplace and as the row's default label. */
  title: string;
  /** Short copy shown under the title. One sentence ideal. */
  description: string;
  /**
   * `single` — exactly one row per code allowed (e.g. you have one IBKR
   * account broker connection). Server refuses POST when a row exists.
   * `multi`  — many rows per code allowed (e.g. one row per Google Drive
   * you connect). User-provided `label` differentiates them.
   */
  cardinality: IntegrationCardinality;
  /**
   * Data-flow direction, used by the FE to gate run controls:
   * `pull` — we fetch on demand, so the card shows a "Sync now" button (IBKR).
   * `push` — data arrives inbound via a webhook, so "Sync now" is a no-op and
   * is hidden; "Test" only validates config (bank_webhook).
   * Defaults to `pull` when omitted.
   */
  direction?: "pull" | "push";
  /** Optional grouping for the marketplace tab/section UI. */
  category?: "broker" | "bank" | "storage" | "tooling";
  /** FE-only icon hint (filename under apps/web/public/icons/, or emoji fallback). */
  icon?: string;
  /**
   * Optional link to the n8n workflow that drives this integration (push
   * integrations fetch/deliver via n8n on k-2). The card renders an
   * external-link icon so you can jump straight to the workflow.
   */
  n8nWorkflowUrl?: string;
  /**
   * Per-instance config fields for **multi-cardinality** integrations. On
   * instance create the server seeds one settings row per field; on delete it
   * tears them down. (Single-cardinality integrations declare their config as
   * static SETTINGS_REGISTRY entries instead — one global value set.)
   */
  configSchema?: readonly ConfigField[];
}

// Hand-keyed map. The values are typed as CatalogEntry; the keys MUST
// match `IntegrationCode` (enforced by the `satisfies` clause below).
export const INTEGRATION_CATALOG = {
  ibkr: {
    title: "Interactive Brokers",
    description:
      "Positions, cash balances, dividends, and trades from IBKR Flex — fetched by the n8n workflow on k-2 and pushed to /ingest/ibkr.",
    cardinality: "single",
    direction: "push",
    category: "broker",
    icon: "🟦",
    n8nWorkflowUrl: "https://n8n.cleveris.app/workflow/TQF1zGd0Up9UfE3m",
  },
  bank_webhook: {
    title: "Bank balance webhook",
    description:
      "Receive already-parsed bank balance snapshots from an external workflow (e.g. n8n) via a signed webhook. One instance per bank source.",
    cardinality: "multi",
    direction: "push",
    category: "bank",
    icon: "🏦",
    n8nWorkflowUrl: "https://n8n.cleveris.app/workflow/v0BGkQXMQqDRp95f",
    configSchema: [
      {
        key: "source",
        title: "Source key",
        description: "The `source` value the upstream poster (n8n) sends — matches this instance's inbound payloads.",
        type: "string",
      },
      {
        key: "signing_secret",
        title: "Signing secret",
        description: "HMAC-SHA256 secret shared with the poster; verifies X-Finance-Signature on each /ingest/bank-balance post.",
        type: "encrypted_string",
      },
      {
        key: "linked_account_ids",
        title: "Linked accounts",
        description: "Bank account(s) this source writes into. The posted account_ref is matched to a linked account's external_account_id (same as IBKR).",
        type: "account_ids",
        accept_account_kinds: ["bank"],
      },
    ],
  },
  // ── Next-up integrations (placeholders — add entry, ship handler + card) ──
  //
  // revolut: {
  //   title: "Revolut",
  //   description: "Open-banking PSD2 sync via Revolut Business.",
  //   cardinality: "single",
  //   category: "bank",
  // },
  // monobank: {
  //   title: "Monobank",
  //   description: "Personal account + cards via api.monobank.ua.",
  //   cardinality: "single",
  //   category: "bank",
  // },
  // wise: {
  //   title: "Wise",
  //   description: "Multi-currency balances + activity via the Wise REST API.",
  //   cardinality: "single",
  //   category: "bank",
  // },
  // gdrive: {
  //   title: "Google Drive",
  //   description: "Index documents + receipts for AI processing.",
  //   cardinality: "multi",
  //   category: "storage",
  // },
} as const satisfies Record<string, CatalogEntry>;

export type CatalogCode = keyof typeof INTEGRATION_CATALOG;

// ───────── Per-instance config helpers (multi-cardinality integrations) ─────────

/** Tag that scopes a settings row to one integration instance (by row id). */
export const INSTANCE_TAG_PREFIX = "integration_instance:";
export function instanceTag(integrationId: string): string {
  return `${INSTANCE_TAG_PREFIX}${integrationId}`;
}

/** Slugify a label into a snake_case fragment safe for a setting code. */
export function settingSlug(label: string): string {
  const s = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return s || "x";
}

/**
 * Per-instance setting code: `${integration}_${labelSlug}_${field}`. Readable
 * and set once at creation — it is NOT recomputed when the label changes, so a
 * rename never orphans the instance's settings.
 */
export function instanceSettingCode(integrationCode: string, labelSlug: string, fieldKey: string): string {
  return `${integrationCode}_${labelSlug}_${fieldKey}`;
}

// Compile-time check that every `IntegrationCode` (declared in codes.ts)
// has a catalog entry. If a new code is added to IntegrationConfigByCode
// without a catalog entry, this assertion fails the build.
type _AllCodesInCatalog = IntegrationCode extends CatalogCode ? true : never;
const _check: _AllCodesInCatalog = true;
void _check;
